import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Search, UploadCloud, History, Megaphone, BarChart3,
  ScrollText, Users, LogOut, Compass, Building2, LayoutDashboard,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard",  label: "Dashboard",       icon: LayoutDashboard, roles: ["admin", "analyst", "campaign_manager"], id: "nav-dashboard" },
  { to: "/search",     label: "Audiences",       icon: Search,     roles: ["admin", "analyst", "campaign_manager"], id: "nav-search" },
  { to: "/upload",     label: "New Query",       icon: UploadCloud, roles: ["admin", "analyst", "campaign_manager"], id: "nav-upload" },
  { to: "/history",    label: "Search History",  icon: History,    roles: ["admin", "analyst", "campaign_manager"], id: "nav-history" },
  { to: "/campaigns/new", label: "Build Campaign", icon: Megaphone, roles: ["admin", "campaign_manager"],            id: "nav-build" },
  { to: "/campaigns",  label: "Campaigns",       icon: Megaphone,  roles: ["admin", "campaign_manager"],            id: "nav-campaigns" },
  { to: "/advertisers", label: "Advertisers",    icon: Building2,  roles: ["admin", "analyst", "campaign_manager"], id: "nav-advertisers" },
  { to: "/reports",    label: "Reports",         icon: BarChart3,  roles: ["admin", "analyst", "campaign_manager"], id: "nav-reports" },
  { to: "/logs",       label: "Integration Logs", icon: ScrollText, roles: ["admin", "analyst", "campaign_manager"], id: "nav-logs" },
  { to: "/users",      label: "Users",           icon: Users,      roles: ["admin"],                                 id: "nav-users" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const items = NAV.filter((i) => i.roles.includes(user.role));

  return (
    <aside
      data-testid="app-sidebar"
      className="w-64 lg:w-72 shrink-0 bg-[#0A0A0A] text-white border-r border-[#2A2A2A] h-full flex flex-col overflow-y-auto"
    >
      <div className="px-6 pt-8 pb-6 border-b border-[#2A2A2A]">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 border-2 border-[#FF3E00] flex items-center justify-center">
            <Compass size={18} className="text-[#FF3E00]" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tighter leading-none">GEO_SYNC</h1>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#888] mt-1">
              Audience · Activation
            </p>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-6 space-y-1">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.id}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 border-l-2",
                  isActive
                    ? "bg-[#1A1A1A] border-[#FF3E00] text-white"
                    : "border-transparent text-[#bbb] hover:bg-[#161616] hover:text-white"
                )
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="px-4 py-5 border-t border-[#2A2A2A]">
        <div className="mb-3">
          <p className="text-xs font-mono uppercase tracking-[0.15em] text-[#888]">
            Signed in
          </p>
          <p className="text-sm font-semibold truncate" data-testid="sidebar-user-name">
            {user.name}
          </p>
          <p className="text-[11px] font-mono text-[#FF3E00] uppercase tracking-wider mt-0.5">
            {user.role.replace("_", " ")}
          </p>
        </div>
        <button
          data-testid="logout-button"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="w-full flex items-center justify-center gap-2 border border-[#2A2A2A] hover:border-[#FF3E00] hover:text-[#FF3E00] text-xs uppercase font-mono tracking-wider py-2.5 transition-colors"
        >
          <LogOut size={14} /> Logout
        </button>
      </div>
    </aside>
  );
}
